import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import type { ComponentPropsWithoutRef, ReactNode } from "react";
import type { Control, FieldPath, FieldValues } from "react-hook-form";

import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "./form";

export interface FormInputProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  control: Control<TFieldValues>;
  name: TName;
  label: string;
  type?: ComponentPropsWithoutRef<typeof Input>["type"];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  description?: string;
  /** Supplemental content displayed alongside the label. Stacks above the input on mobile. */
  labelAside?: ReactNode;
  /** Optional className override for the label aside container. */
  labelAsideClassName?: string;
  /** Marks field as required (shows asterisk) */
  required?: boolean;
  /** Auto-focus this input on mount */
  autoFocus?: boolean;
  /** Maximum character length - shows counter when set */
  maxLength?: number;
  /** Show character counter even without maxLength */
  showCharacterCount?: boolean;
  /** Input pattern for HTML validation */
  pattern?: string;
  /** Accessible label for screen readers */
  "aria-label"?: string;
  /** Transform the input value on every change (e.g. autoformatting). Return the formatted string. */
  formatValue?: (value: string) => string;
}

/**
 * Form-connected input field with enhanced features
 *
 * @description Standard text input with support for character counting,
 * auto-focus, and various input types. Mobile-first responsive design.
 *
 * @example Basic usage
 * ```tsx
 * <FormInput
 *   control={form.control}
 *   name="email"
 *   label="Email"
 *   type="email"
 *   placeholder="dev@example.com"
 * />
 * ```
 *
 * @example With character limit
 * ```tsx
 * <FormInput
 *   control={form.control}
 *   name="username"
 *   label="Username"
 *   maxLength={20}
 *   required
 * />
 * ```
 *
 * @example With auto-focus
 * ```tsx
 * <FormInput
 *   control={form.control}
 *   name="search"
 *   label="Search"
 *   autoFocus
 * />
 * ```
 */
export function FormInput<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  control,
  name,
  label,
  type = "text",
  placeholder,
  disabled = false,
  className,
  description,
  labelAside,
  labelAsideClassName,
  required,
  autoFocus,
  maxLength,
  showCharacterCount,
  pattern,
  "aria-label": ariaLabel,
  formatValue,
}: FormInputProps<TFieldValues, TName>) {
  const shouldShowCounter = maxLength !== undefined || showCharacterCount;

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        const charCount = typeof field.value === "string" ? field.value.length : 0;

        return (
          <FormItem className={className}>
            {labelAside ? (
              <div className="flex flex-col gap-2 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4">
                <FormLabel required={required}>{label}</FormLabel>
                <div className={cn("sm:max-w-[60%]", labelAsideClassName)}>{labelAside}</div>
              </div>
            ) : (
              <FormLabel required={required}>{label}</FormLabel>
            )}
            <FormControl>
              <Input
                type={type}
                placeholder={placeholder}
                disabled={disabled}
                autoFocus={autoFocus}
                maxLength={maxLength}
                pattern={pattern}
                aria-label={ariaLabel}
                {...field}
                onChange={formatValue
                  ? (e) => field.onChange(formatValue(e.target.value))
                  : field.onChange
                }
              />
            </FormControl>
            <div
              className={cn(
                "flex items-center gap-2",
                shouldShowCounter && !description ? "justify-end" : "justify-between"
              )}
            >
              {description && <FormDescription>{description}</FormDescription>}
              {shouldShowCounter && (
                <span
                  className={cn(
                    "text-xs tabular-nums",
                    maxLength && charCount >= maxLength ? "text-destructive" : "text-muted-foreground"
                  )}
                >
                  {charCount}
                  {maxLength && `/${maxLength}`}
                </span>
              )}
            </div>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}

